import { useEffect } from 'react'
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useUIStore } from '@/store/uiStore'

/**
 * Renderer globale dei toast dello uiStore (montato in main.tsx).
 * Ogni toast si auto-rimuove dopo 6 secondi, oppure con la X.
 */

const TOAST_TTL_MS = 6_000

const STYLE_BY_TYPE = {
  success: { icon: CheckCircle2,  box: 'border-green-200 bg-green-50',  iconColor: 'text-green-600'  },
  error:   { icon: XCircle,       box: 'border-red-200 bg-red-50',      iconColor: 'text-red-600'    },
  info:    { icon: Info,          box: 'border-holly-200 bg-holly-50',  iconColor: 'text-holly-600'  },
  warning: { icon: AlertTriangle, box: 'border-amber-200 bg-amber-50',  iconColor: 'text-amber-600'  },
} as const

function ToastItem({ id, type, title, message }: {
  id: string
  type: keyof typeof STYLE_BY_TYPE
  title: string
  message?: string
}) {
  const removeToast = useUIStore((s) => s.removeToast)

  useEffect(() => {
    const timer = setTimeout(() => removeToast(id), TOAST_TTL_MS)
    return () => clearTimeout(timer)
  }, [id, removeToast])

  const { icon: Icon, box, iconColor } = STYLE_BY_TYPE[type] ?? STYLE_BY_TYPE.info

  return (
    <div
      role="status"
      className={`pointer-events-auto flex w-80 items-start gap-3 rounded-xl border p-4 shadow-lg ${box}`}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconColor}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {message && <p className="mt-0.5 text-sm text-gray-600">{message}</p>}
      </div>
      <button
        type="button"
        aria-label="Chiudi notifica"
        onClick={() => removeToast(id)}
        className="shrink-0 rounded-md p-0.5 text-gray-400 transition-colors hover:text-gray-600"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function ToastContainer() {
  const toasts = useUIStore((s) => s.toasts)
  if (toasts.length === 0) return null

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.id} {...t} />
      ))}
    </div>
  )
}
